import { isSignerMutationRequest } from './authority';
import { refreshTradingSessionStatus } from './statusRefresh';
import { useTradingSessionStore } from './store';

export class TradingAuthorityRequiredError extends Error {
    constructor() {
        super('Trading authority required');
        this.name = 'TradingAuthorityRequiredError';
    }
}

export function isTradingAuthorityRequiredError(error: unknown): boolean {
    return (
        error instanceof TradingAuthorityRequiredError ||
        (typeof error === 'object' &&
            error !== null &&
            (error as { name?: unknown }).name ===
                'TradingAuthorityRequiredError')
    );
}

/**
 * Authority is gone: the app falls back to its signed-out demo state (real mode
 * is derived from the same status), and the user signs in when they choose to.
 * Nothing pops a dialog here any more; a request that needed authority simply
 * fails with the sign-in message.
 */
function surfaceMissingAuthority(): void {
    void refreshTradingSessionStatus();
}

export async function assertTradingAuthorityForRequest(
    request: Request,
    localNowMs = Date.now(),
): Promise<void> {
    const url = new URL(request.url);
    if (!isSignerMutationRequest(request.method, url.pathname)) return;

    if (useTradingSessionStore.getState().hasActiveAuthority(localNowMs)) {
        return;
    }

    // The store holds a copy of the server's answer, and an OAuth-backed
    // authority expires with the provider token the server refreshes silently.
    // A copy that has gone stale reads exactly like a session that ended, so
    // ask the server once before telling the user their authority is gone —
    // this is the whole 2026-08-23 incident, where editing a stop-loss demanded
    // a trading window for a session the backend was still accepting.
    await refreshTradingSessionStatus();

    if (useTradingSessionStore.getState().hasActiveAuthority(localNowMs)) {
        return;
    }

    throw new TradingAuthorityRequiredError();
}

export function handleTradingAuthorityRequired(): void {
    surfaceMissingAuthority();
}
